import { useEffect, useMemo, useState } from 'react';
import type { CwDataset } from '@catalyst-psa/shared';
import { CW_DATASET_LABELS } from '@catalyst-psa/shared';

import { Modal } from '../../components/ui/Modal.js';
import { Button } from '../../components/ui/Button.js';
import { CustomerTypeAliasEditor } from './CustomerTypeAliasEditor.js';
import { ProjectTypeAliasEditor } from './ProjectTypeAliasEditor.js';
import { ProjectStatusAliasEditor } from './ProjectStatusAliasEditor.js';
import { OpportunityStageAliasEditor } from './OpportunityStageAliasEditor.js';
import { OpportunityStatusAliasEditor } from './OpportunityStatusAliasEditor.js';
import { OpportunityTypeAliasEditor } from './OpportunityTypeAliasEditor.js';
import { OpportunitySalesRepAliasEditor } from './OpportunitySalesRepAliasEditor.js';
import { ServiceCategoryAliasEditor } from './ServiceCategoryAliasEditor.js';
import { ProductClassAliasEditor } from './ProductClassAliasEditor.js';
import { AgreementSubtypeAliasEditor } from './AgreementSubtypeAliasEditor.js';
import { AgreementStatusAliasEditor } from './AgreementStatusAliasEditor.js';
import { WorkRoleAliasEditor } from './WorkRoleAliasEditor.js';
// Time-entry service category is resolved from the CW work role (shared work-role alias).
import { TicketStatusAliasEditor } from './TicketStatusAliasEditor.js';
import { ProjectTicketStatusAliasEditor } from './ProjectTicketStatusAliasEditor.js';
import { TicketTypeAliasEditor } from './TicketTypeAliasEditor.js';
import { ChargeCodeAliasEditor } from './ChargeCodeAliasEditor.js';
import { WorkTypeBillingClassAliasEditor } from './WorkTypeBillingClassAliasEditor.js';
import { TimesheetStatusAliasEditor } from './TimesheetStatusAliasEditor.js';
import { useCwDatasetMappings, useCwSourceFields, useSetCwDatasetMappings } from './api.js';

interface FieldMappingModalProps {
  dataset: CwDataset;
  isOpen: boolean;
  onClose: () => void;
}

interface RowState {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  targetField: string;
  sourceField: string;
  enabled: boolean;
}

let rowSeq = 0;
function newRowId(): string {
  rowSeq += 1;
  return `row-${rowSeq}`;
}

export function FieldMappingModal({
  dataset,
  isOpen,
  onClose,
}: FieldMappingModalProps): JSX.Element {
  const configQuery = useCwDatasetMappings(dataset, isOpen);
  const sourceFieldsQuery = useCwSourceFields(dataset, isOpen);
  const setMappings = useSetCwDatasetMappings();

  const [rows, setRows] = useState<RowState[]>([]);

  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.mappings.map((mapping) => ({
        id: newRowId(),
        targetField: mapping.targetField,
        sourceField: mapping.sourceField,
        enabled: mapping.enabled,
      })),
    );
  }, [config]);

  const datalistId = `cw-sources-${dataset}`;

  // Prefer live-discovered CW fields; fall back to the static suggestions.
  const sourceFields = sourceFieldsQuery.data?.fields ?? config?.sourceFields ?? [];
  const sourceIsLive = sourceFieldsQuery.data?.live ?? false;

  const usedTargets = useMemo(
    () => new Set(rows.map((row) => row.targetField).filter(Boolean)),
    [rows],
  );

  const updateRow = (id: string, patch: Partial<RowState>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (): void => {
    setRows((prev) => [
      ...prev,
      { id: newRowId(), targetField: '', sourceField: '', enabled: true },
    ]);
  };

  const handleSave = (): void => {
    const mappings = rows
      .filter((row) => row.targetField)
      .map((row) => ({
        targetField: row.targetField,
        sourceField: row.sourceField.trim(),
        enabled: row.enabled,
      }));
    setMappings.mutate({ dataset, input: { mappings } }, { onSuccess: () => onClose() });
  };

  const targetFields = config?.targetFields ?? [];
  const mappedTargetCount = rows.filter((row) => row.targetField).length;
  const hasDuplicate = usedTargets.size !== mappedTargetCount;
  const missingRequired = targetFields
    .filter((field) => field.required)
    .filter((field) => !usedTargets.has(field.key));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`${CW_DATASET_LABELS[dataset]} field mapping`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={setMappings.isPending}
            disabled={!config || hasDuplicate}
          >
            Save mapping
          </Button>
        </div>
      }
    >
      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading mapping…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load the mapping.</p>
      ) : config && targetFields.length === 0 ? (
        <p className="text-sm text-slate-500">
          Field mapping for this dataset is not available yet.
        </p>
      ) : config ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Add a row for each field you want to import. Pick a Catalyst field and the ConnectWise
            source field that feeds it. New Catalyst fields appear here automatically.
          </p>

          {sourceFieldsQuery.isFetching ? (
            <p className="text-xs text-slate-400">Discovering ConnectWise fields…</p>
          ) : sourceIsLive ? (
            <p className="text-xs text-emerald-600">
              ConnectWise field suggestions loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              Showing built-in field suggestions. Configure the ConnectWise connection to load real
              fields. You can still type any field path.
            </p>
          )}

          <datalist id={datalistId}>
            {sourceFields.map((source) => (
              <option key={source.key} value={source.key}>
                {source.label}
              </option>
            ))}
          </datalist>

          {setMappings.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the mapping. Check that required fields are mapped and there are no
              duplicates.
            </div>
          ) : null}
          {hasDuplicate ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Each Catalyst field can only be mapped once. Remove the duplicate rows.
            </div>
          ) : missingRequired.length > 0 ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Required field{missingRequired.length > 1 ? 's' : ''} not mapped:{' '}
              {missingRequired.map((field) => field.label).join(', ')}.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No mappings yet. Add one to get started.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">Catalyst field</th>
                  <th className="py-2 pr-3 font-medium">ConnectWise field</th>
                  <th className="py-2 pr-2 font-medium">Active</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.targetField}
                        onChange={(e) => updateRow(row.id, { targetField: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select field —</option>
                        {targetFields.map((field) => (
                          <option
                            key={field.key}
                            value={field.key}
                            disabled={field.key !== row.targetField && usedTargets.has(field.key)}
                          >
                            {field.label}
                            {field.required ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.sourceField}
                        placeholder="e.g. status.name"
                        onChange={(e) => updateRow(row.id, { sourceField: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => updateRow(row.id, { enabled: e.target.checked })}
                        className="mt-2 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        title="Remove mapping"
                        className="mt-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div>
            <Button variant="secondary" onClick={addRow}>
              Add mapping
            </Button>
          </div>

          {dataset === 'customers' ? <CustomerTypeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'products' ? <ServiceCategoryAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'products' ? <ProductClassAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'projects' ? <ProjectTypeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'projects' ? <ProjectStatusAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'opportunities' ? <OpportunityStageAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'opportunities' ? <OpportunityStatusAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'opportunities' ? <OpportunityTypeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'opportunities' ? <OpportunitySalesRepAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'agreements' ? <AgreementSubtypeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'agreements' ? <AgreementStatusAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'agreements' ? <WorkRoleAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'tickets' ? <TicketStatusAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'tickets' ? <ProjectTicketStatusAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'tickets' ? <TicketTypeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'timeEntries' ? <WorkRoleAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'timeEntries' ? <WorkTypeBillingClassAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'timeEntries' ? <ChargeCodeAliasEditor isOpen={isOpen} /> : null}
          {dataset === 'timeEntries' ? <TimesheetStatusAliasEditor isOpen={isOpen} /> : null}
        </div>
      ) : null}
    </Modal>
  );
}
